import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Inbox,
  Loader2,
  LogOut,
  Lock,
  Mail,
  RefreshCw,
  Reply,
  Send,
} from "lucide-react";
import { fadeUp, staggerContainer } from "@/lib/motion";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "helm.token";

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/* --------------------------------- Locked --------------------------------- */

function Locked() {
  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-[#E9EEF5] px-6" data-testid="helm-inbox-locked">
      <div className="card-light w-full max-w-sm rounded-sm p-8 text-center">
        <span className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340] text-white">
          <Lock size={18} strokeWidth={1.6} />
        </span>
        <h1 className="font-display text-2xl text-[#0C2340]">Locked</h1>
        <p className="mt-3 font-body text-sm text-[#1E3A5F]">
          Sign in through The Helm to access the inbox.
        </p>
        <Link
          to="/the-helm"
          className="btn-ink mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm px-6 py-3 font-mono-2 text-[0.72rem] uppercase tracking-[0.2em]"
          data-testid="helm-inbox-gate-link"
        >
          Go to The Helm
        </Link>
      </div>
    </div>
  );
}

/* --------------------------------- Inbox ---------------------------------- */

function InboxView({ token, onSignOut }) {
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [replyForId, setReplyForId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyStatus, setReplyStatus] = useState({});

  const openReply = (id) => {
    setReplyForId(id);
    setReplyText("");
    setReplyStatus((s) => ({ ...s, [id]: null }));
  };

  const sendReply = async (c) => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      const resp = await fetch(`${API}/api/helm/contacts/${c.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Helm-Token": token },
        body: JSON.stringify({ message: replyText }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Send failed");
      }
      const data = await resp.json();
      setContacts((list) =>
        list.map((x) => (x.id === c.id ? { ...x, replied_at: data.replied_at } : x))
      );
      setReplyStatus((s) => ({ ...s, [c.id]: { ok: true } }));
      setReplyForId(null);
      setReplyText("");
    } catch (err) {
      setReplyStatus((s) => ({ ...s, [c.id]: { ok: false, msg: err.message } }));
    } finally {
      setSending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API}/api/helm/contacts`, {
        headers: { "X-Helm-Token": token },
      });
      if (resp.status === 401) {
        onSignOut();
        return;
      }
      if (!resp.ok) throw new Error("Failed to load");
      setContacts(await resp.json());
    } catch (err) {
      setError("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total = contacts?.length || 0;
  const last7d = useMemo(() => {
    if (!contacts) return 0;
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return contacts.filter((c) => {
      const t = Date.parse(c.created_at);
      return !Number.isNaN(t) && t >= cutoff;
    }).length;
  }, [contacts]);

  return (
    <div className="grain relative min-h-screen bg-[#E9EEF5] pb-24" data-testid="helm-inbox">
      {/* Top bar */}
      <header className="sticky top-0 z-30 glass-nav-light">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Tide Water Studios" className="h-9 w-9 object-cover logo-medallion" />
            <div>
              <p className="overline overline-dark text-[0.6rem]">/ The Helm — Inbox</p>
              <h1 className="font-display text-xl text-[#0C2340]">Consultation Requests</h1>
              <p className="font-mono-2 mt-0.5 text-[0.58rem] uppercase tracking-[0.18em] text-[#2E4A68]" data-testid="helm-inbox-corp-meta">
                BN 846581 &middot; Incorporated 20 Jul 2026
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/the-helm"
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-inbox-back"
            >
              <ArrowLeft size={12} />
              Dashboard
            </Link>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-inbox-refresh"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={onSignOut}
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-inbox-signout"
            >
              <LogOut size={12} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 pt-12 md:px-10">
        {/* Intro / summary */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="mb-12"
        >
          <motion.p variants={fadeUp} className="overline overline-dark mb-5">/ Inbox</motion.p>
          <motion.h2 variants={fadeUp} className="font-display text-5xl tracking-tight text-[#0C2340] md:text-6xl">
            Lead <span>Log</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-2xl font-body text-base leading-relaxed text-[#1E3A5F]">
            Every consultation request from the site lands here, newest first.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="card-light rounded-sm p-5">
              <p className="overline overline-dark">Total</p>
              <p className="mt-2 font-display text-3xl text-[#0C2340]" data-testid="helm-inbox-total">{loading ? "—" : total}</p>
            </div>
            <div className="card-light rounded-sm p-5">
              <p className="overline overline-dark">Last 7 Days</p>
              <p className="mt-2 font-display text-3xl text-[#0C2340]" data-testid="helm-inbox-last7">{loading ? "—" : last7d}</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Table */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {loading ? (
            <div className="card-light flex h-[300px] items-center justify-center rounded-sm text-[#1E3A5F]">
              <Loader2 className="animate-spin" size={22} />
            </div>
          ) : error ? (
            <div className="card-light flex h-[300px] items-center justify-center rounded-sm font-mono-2 text-[0.7rem] uppercase tracking-[0.2em] text-[#8b2b2b]">
              {error}
            </div>
          ) : total === 0 ? (
            <motion.div variants={fadeUp} className="card-light flex flex-col items-center justify-center gap-3 rounded-sm p-14 text-center" data-testid="helm-inbox-empty">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                <Inbox size={22} strokeWidth={1.4} />
              </span>
              <p className="font-display text-2xl text-[#0C2340]">No submissions yet.</p>
              <p className="font-body text-sm text-[#1E3A5F]">Consultation requests will appear here as they come in.</p>
            </motion.div>
          ) : (
            <motion.div variants={fadeUp} className="card-light overflow-hidden rounded-sm">
              <table className="w-full" data-testid="helm-inbox-table">
                <thead>
                  <tr className="border-b border-[#0C2340]/10 bg-[#0C2340]/[0.03]">
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Received</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Name</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Email</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Company</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Interest</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Message</th>
                    <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => {
                    const expanded = expandedId === c.id;
                    const replying = replyForId === c.id;
                    const status = replyStatus[c.id];
                    return (
                      <Fragment key={c.id}>
                        <tr
                          onClick={() => setExpandedId(expanded ? null : c.id)}
                          className={`cursor-pointer border-b border-[#0C2340]/5 transition-colors hover:bg-[#0C2340]/[0.02] ${
                            expanded ? "bg-[#0C2340]/[0.03]" : ""
                          }`}
                          data-testid={`helm-inbox-row-${c.id}`}
                        >
                          <td className="px-6 py-4 align-top font-mono-2 text-[0.7rem] text-[#2E4A68]">{fmtDate(c.created_at)}</td>
                          <td className="px-6 py-4 align-top font-body text-[#0C2340]">{c.name}</td>
                          <td className="px-6 py-4 align-top">
                            <a
                              href={`mailto:${c.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-2 font-body text-[#1E3A5F] hover:text-[#0C2340]"
                            >
                              <Mail size={12} />
                              {c.email}
                            </a>
                          </td>
                          <td className="px-6 py-4 align-top font-body text-[#1E3A5F]">{c.company || "—"}</td>
                          <td className="px-6 py-4 align-top font-body text-[#1E3A5F]">{c.service || "—"}</td>
                          <td className="px-6 py-4 align-top font-body text-[#1E3A5F]">
                            {expanded ? (
                              <div className="whitespace-pre-wrap">{c.message}</div>
                            ) : (
                              <div className="line-clamp-2 max-w-md">{c.message}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col items-start gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  replying ? setReplyForId(null) : openReply(c.id);
                                }}
                                className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-1.5 font-mono-2 text-[0.62rem] uppercase tracking-[0.18em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
                                data-testid={`helm-inbox-reply-btn-${c.id}`}
                              >
                                <Reply size={12} />
                                Reply
                              </button>
                              {c.replied_at && (
                                <span
                                  className="flex items-center gap-1.5 font-mono-2 text-[0.58rem] uppercase tracking-[0.15em] text-[#2E7D52]"
                                  data-testid={`helm-inbox-replied-badge-${c.id}`}
                                >
                                  <Check size={11} />
                                  Replied
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {replying && (
                          <tr className="border-b border-[#0C2340]/5 bg-[#0C2340]/[0.02]" data-testid={`helm-inbox-reply-row-${c.id}`}>
                            <td colSpan={7} className="px-6 py-5">
                              <p className="mb-2 font-mono-2 text-[0.62rem] uppercase tracking-[0.18em] text-[#2E4A68]">
                                Replying to {c.name} &lt;{c.email}&gt; — from sean@tidewaterstudios.ca
                              </p>
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                rows={5}
                                placeholder="Write your response…"
                                className="w-full rounded-sm border border-[#0C2340]/15 bg-white p-4 font-body text-sm text-[#0C2340] outline-none transition-colors focus:border-[#0C2340]/45"
                                data-testid={`helm-inbox-reply-textarea-${c.id}`}
                              />
                              <div className="mt-3 flex items-center gap-3">
                                <button
                                  onClick={() => sendReply(c)}
                                  disabled={sending || !replyText.trim()}
                                  className="btn-ink flex items-center gap-2 rounded-sm px-5 py-2.5 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] disabled:cursor-not-allowed disabled:opacity-50"
                                  data-testid={`helm-inbox-reply-send-${c.id}`}
                                >
                                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                  {sending ? "Sending…" : "Send"}
                                </button>
                                <button
                                  onClick={() => setReplyForId(null)}
                                  className="rounded-sm border border-[#0C2340]/15 bg-white px-4 py-2.5 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] hover:border-[#0C2340]/40"
                                  data-testid={`helm-inbox-reply-cancel-${c.id}`}
                                >
                                  Cancel
                                </button>
                                {status && !status.ok && (
                                  <span className="font-mono-2 text-[0.62rem] uppercase tracking-[0.15em] text-[#8b2b2b]" data-testid={`helm-inbox-reply-error-${c.id}`}>
                                    {status.msg || "Send failed"}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          )}
        </motion.section>
      </div>
    </div>
  );
}

/* ---------------------------------- Page ---------------------------------- */

export default function HelmInbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");

  useEffect(() => {
    document.title = "The Helm — Inbox";
  }, []);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <Locked />;
  return <InboxView token={token} onSignOut={signOut} />;
}
